import { ReconnectTasks } from '../../components/ReconnectTasks';
import { CsvExportButton, useCsvExport } from '../../components/CsvExport';
import { useViewState } from '../../lib/useViewState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reportError } from '../../lib/errorEvents';
import { melbourneToday, weekOf } from '../../../shared/dates';
import { OUTREACH_LEAD_STATUSES, OUTREACH_VERTICALS, type OutreachLeadRow, type OutreachLeadStatus, type OutreachVertical } from '../../../shared/types';
import { formatDayMonth, PageHeader, SkeletonRows, Vital, Vitals } from '../../components/primitives';
import { FilterSheet } from '../../components/FilterSheet';
import { DotsIcon, FilterIcon } from '../../components/icons';
import { PageMenu } from '../../components/PageMenu';
import { outreachHeaderCount, outreachWeekLine } from '../../lib/outreachMobile';
import { useIsMobile } from '../../lib/useIsMobile';
import { STATUS_LABELS, VERTICAL_LABELS, COUNTER_TILES } from './constants';
import { outreachCounters, rankLeads, filterLeads, outreachViewCodec, outreachRunParam } from './helpers';
import type { OutreachFilters, OutreachActions } from './types';
import { fetchOutreachLeads, applyStatus, deleteLead } from './api';
import { OutreachListBody } from './OutreachListBody';
import { OutreachDossier } from './OutreachDossier';
import { PromoteModal } from './PromoteModal';
import { OutreachEmpty } from './chrome';

// Outreach tab — the weekly cold-lead dossiers the lead scraper pushes,
// worked with one-click statuses. Built to the eight approved mockups in
// docs/2026-08-11-outreach-tab-ui-mockups.html (Henry-approved 2026-08-11)
// over the /api/admin/outreach-leads routes.
//
// The app owns statuses: one PATCH per click, the list refetches, and the
// sidebar badge follows (no optimistic UI). `new` is the arrival state only,
// so nothing here can write it back. The dossier text, links and contacts are
// display-only JSON the scraper wrote, so every read of them is defensive:
// a lead with a missing email says so rather than inventing one.

export default function Outreach() {
  const { initial, publish } = useViewState(outreachViewCodec, state => {
    setFilters({ statuses: state.statuses, vertical: state.vertical, runDate: state.run === 'all' ? null : state.run });
    setRunPinned(state.run !== null);
    setUserPinnedRun(state.run !== null);
    setShortlistOpen(state.shortlistOpen);
    setQueuedOpen(state.queuedOpen);
  });
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const today = melbourneToday();
  const [leads, setLeads] = useState<OutreachLeadRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState<OutreachFilters>(() => ({
    statuses: initial.statuses,
    vertical: initial.vertical,
    runDate: initial.run === 'all' ? null : initial.run,
  }));
  // The latest run is the default view; only pinned once, so a chip Henry
  // picks himself survives every later refetch.
  const [runPinned, setRunPinned] = useState(() => initial.run !== null);
  const [userPinnedRun, setUserPinnedRun] = useState(() => initial.run !== null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [shortlistOpen, setShortlistOpen] = useState(() => initial.shortlistOpen);
  const [queuedOpen, setQueuedOpen] = useState(() => initial.queuedOpen);

  useEffect(() => {
    publish({
      statuses: filters.statuses,
      vertical: filters.vertical,
      run: outreachRunParam(filters.runDate, userPinnedRun),
      shortlistOpen,
      queuedOpen,
    });
  }, [publish, filters.statuses, filters.vertical, filters.runDate, userPinnedRun, shortlistOpen, queuedOpen]);
  const [promoting, setPromoting] = useState<OutreachLeadRow | null>(null);

  const fetchSequence = useRef(0);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    const sequence = ++fetchSequence.current;
    fetchOutreachLeads()
      .then(rows => { if (sequence === fetchSequence.current) setLeads(rows); })
      .catch(() => {})
      .finally(() => { if (sequence === fetchSequence.current) setLoaded(true); });
  }, []);
  useEffect(() => {
    refresh();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(refresh, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      ++fetchSequence.current;
    };
  }, [refresh]);

  const runDates = useMemo(
    () => [...new Set(leads.map((l) => l.run_date))].sort().reverse(),
    [leads],
  );

  useEffect(() => {
    if (runPinned || runDates.length === 0) return;
    setRunPinned(true);
    setFilters((f) => ({ ...f, runDate: runDates[0] }));
  }, [runDates, runPinned]);

  const counters = useMemo(() => outreachCounters(leads, today), [leads, today]);
  // Ranked within the selected run, so rank 04 means the fourth lead that run
  // pushed, whatever the other chips are doing.
  const ranked = useMemo(
    () => rankLeads(filters.runDate === null ? leads : leads.filter((l) => l.run_date === filters.runDate)),
    [leads, filters.runDate],
  );
  const visible = useMemo(() => filterLeads(ranked, filters), [ranked, filters]);
  const csvExport = useCsvExport('outreach', { view: 'outreach', statuses: filters.statuses.join(','), vertical: filters.vertical, run_date: filters.runDate }, {
    count: visible.length, filterLabels: {
      statuses: filters.statuses.map(s => STATUS_LABELS[s]).join(', '),
      vertical: filters.vertical ? VERTICAL_LABELS[filters.vertical] : undefined,
      run_date: filters.runDate ? formatDayMonth(filters.runDate) : undefined,
    },
  });
  const totals = useMemo(
    () => ({
      shortlist: ranked.filter((e) => e.lead.verdict === 'shortlist').length,
      queued: ranked.filter((e) => e.lead.verdict === 'queued').length,
    }),
    [ranked],
  );
  const openEntry = openId === null ? null : (ranked.find((e) => e.lead.id === openId) ?? null);

  useEffect(() => {
    if (openId === null || isMobile) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, isMobile]);

  /** Every mutation runs the same way: one request, then a full refetch —
      the row never moves before the API says it did. */
  const mutate = useCallback(
    async (run: () => Promise<void>) => {
      await run().catch((error) => reportError(error, 'Could not update the outreach lead.'));
      refresh();
    },
    [refresh],
  );

  const actions: OutreachActions = {
    onOpenLead: (id) => setOpenId((current) => (current === id ? null : id)),
    onStatus: (lead, status, closedReason) =>
      void mutate(() => applyStatus(lead, status, closedReason ?? null)),
    onPromote: (lead) => setPromoting(lead),
    onDelete: (lead) => {
      setOpenId(null);
      void mutate(() => deleteLead(lead));
    },
  };

  const total = leads.length;
  const countLabel = visible.length === total ? total : `${visible.length} of ${total}`;
  const subLabel = filters.runDate === null
    ? 'cold leads'
    : `cold leads · week of ${formatDayMonth(weekOf(filters.runDate).start)}`;
  /** The view the page opens on: newest run, every status, every vertical. */
  const defaultRunDate = runDates[0] ?? null;
  const isDefaultFilter =
    filters.statuses.length === 0 && filters.vertical === null && filters.runDate === defaultRunDate;
  const onlyClosed = filters.statuses.length === 1 && filters.statuses[0] === 'closed';

  return (
    <>
      <PageHeader
        title="Outreach"
        count={isMobile ? outreachHeaderCount(visible.length, total) : countLabel}
        mobileActions={
          isMobile
            ? [
                {
                  key: 'filter',
                  label: 'Filter outreach',
                  icon: <FilterIcon />,
                  active: !isDefaultFilter,
                  onPress: () => setShowFilters(true),
                },
                {
                  key: 'menu',
                  label: 'More outreach actions',
                  icon: <DotsIcon />,
                  onPress: () => setShowMenu(true),
                },
              ]
            : undefined
        }
      >
        {/* The mono sub-line moves under the header on mobile (frame A). */}
        {!isMobile && <span className="outreach-sub">{subLabel}</span>}
      </PageHeader>

      <div className="page-body">
        <div className={`content wide${isMobile ? ' outreach-mobile' : ''}`}>
          <ReconnectTasks/>
          {isMobile && (
            <div className="outreach-mobile-week">{outreachWeekLine(filters.runDate)}</div>
          )}
          {!loaded ? (
            <SkeletonRows rows={6} />
          ) : total === 0 ? (
            <OutreachEmpty />
          ) : (
            <>
              {isMobile ? (
                // Five tiles across a 393px screen, so each one carries the
                // week number and a one-word label — the "N all time" sub-line
                // the desktop vitals show has no room here (frame A).
                <div className="outreach-mobile-counters">
                  {COUNTER_TILES.map((tile) => (
                    <div
                      key={tile.key}
                      className={`outreach-mobile-counter${tile.tone ? ` ${tile.tone}` : ''}`}
                    >
                      <div className="outreach-mobile-number">{counters[tile.key].week}</div>
                      <div className="outreach-mobile-label">{tile.short ?? tile.label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <Vitals>
                {COUNTER_TILES.map((tile) => (
                  <Vital
                    key={tile.key}
                    label={tile.label}
                    value={counters[tile.key].week}
                    tone={tile.tone}
                    sub={`${counters[tile.key].all} all time`}
                  />
                ))}
                </Vitals>
              )}

              <OutreachListBody
                exportAction={<CsvExportButton state={csvExport} />}
                ranked={visible}
                totals={totals}
                filters={filters}
                onFilter={(next, runSelected) => {
                  if (runSelected) {
                    setRunPinned(true);
                    setUserPinnedRun(true);
                  }
                  setFilters(next);
                }}
                runDates={runDates}
                shortlistOpen={shortlistOpen}
                queuedOpen={queuedOpen}
                onToggleSection={(section) =>
                  section === 'shortlist' ? setShortlistOpen((o) => !o) : setQueuedOpen((o) => !o)
                }
                actions={actions}
              />
            </>
          )}
        </div>
      </div>

      {isMobile && (
        <>
          <FilterSheet
            open={showFilters}
            onClose={() => setShowFilters(false)}
            resultLabel={`Show ${visible.length} lead${visible.length === 1 ? '' : 's'}`}
            onApply={() => setShowFilters(false)}
            // "Clear" means the view the page opens on: newest run, no chips.
            onClear={() => {
              setRunPinned(false);
              setUserPinnedRun(false);
              setFilters({ statuses: [], vertical: null, runDate: defaultRunDate });
            }}
            sections={[
              {
                key: 'status',
                label: 'Status',
                kind: 'chips',
                multi: true,
                value: filters.statuses,
                options: OUTREACH_LEAD_STATUSES.map((value) => ({
                  value,
                  label: STATUS_LABELS[value],
                })),
                onChange: (statuses: OutreachLeadStatus[]) =>
                  setFilters((f) => ({ ...f, statuses })),
              },
              {
                key: 'vertical',
                label: 'Vertical',
                kind: 'chips',
                value: filters.vertical ?? '',
                options: [
                  { value: '', label: 'All' },
                  ...OUTREACH_VERTICALS.map((value) => ({ value, label: VERTICAL_LABELS[value] })),
                ],
                onChange: (vertical: OutreachVertical | '') =>
                  setFilters((f) => ({ ...f, vertical: vertical || null })),
              },
              {
                key: 'run',
                label: 'Run',
                kind: 'picker',
                value: filters.runDate,
                placeholder: 'All runs',
                items: [
                  { value: '', label: 'All runs' },
                  ...runDates.map((value) => ({ value, label: formatDayMonth(value) })),
                ],
                onChange: (runDate) => {
                  setRunPinned(true);
                  setUserPinnedRun(true);
                  setFilters((f) => ({ ...f, runDate: runDate || null }));
                },
              },
            ]}
          />
          <PageMenu
            open={showMenu}
            onClose={() => setShowMenu(false)}
            title="Outreach actions"
            items={[
              csvExport.item,
              {
                key: 'closed',
                label: onlyClosed ? 'Show every status' : 'Only closed leads',
                onSelect: () =>
                  setFilters((f) => ({ ...f, statuses: onlyClosed ? [] : ['closed'] })),
              },
              {
                key: 'shortlist',
                label: `${shortlistOpen ? 'Collapse' : 'Expand'} shortlist`,
                onSelect: () => setShortlistOpen((o) => !o),
              },
              {
                key: 'queued',
                label: `${queuedOpen ? 'Collapse' : 'Expand'} queued`,
                onSelect: () => setQueuedOpen((o) => !o),
              },
            ]}
          />
        </>
      )}

      {openEntry !== null &&
        (isMobile ? (
          // The sheet is its own overlay — no veil, and Escape belongs to Sheet.
          <OutreachDossier entry={openEntry} actions={actions} onDismiss={() => setOpenId(null)} />
        ) : (
          <div
            className="outreach-veil"
            onMouseDown={(e) => e.target === e.currentTarget && setOpenId(null)}
          >
            <OutreachDossier entry={openEntry} actions={actions} onDismiss={() => setOpenId(null)} />
          </div>
        ))}

      {promoting !== null && (
        <PromoteModal
          lead={promoting}
          onClose={() => setPromoting(null)}
          onConfirm={() => {
            setPromoting(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
